import { isAxiosError } from 'axios';
import { useConfigQuery } from '../network/queries/useConfigQuery';
import { usePersonasConfigQuery } from '../network/queries/usePersonasConfigQuery';

const RETRY_LIMIT = 3;

/**
 * Reads the per-workspace Developer Personas opt-in from the engine config
 * response. Personas is a Research Preview feature, enabled via the
 * `research_preview.enable_developer_personas` toggle (default off). The
 * platform merges catalog defaults, so an absent value reads as off.
 */
const isDeveloperPersonasToggledOn = (
  config: Record<string, unknown> | undefined,
): boolean => {
  const researchPreview = config?.research_preview as
    | Record<string, unknown>
    | undefined;

  return researchPreview?.enable_developer_personas === true;
};

/**
 * Returns true when a 503 from the personas service signals the feature is
 * disabled platform-side. The qodo-platform `enforce_personas_enabled`
 * dependency returns `503 Service Unavailable` (with `Retry-After`) on every
 * persona GET when the `personas.enabled` master flag is off — that 503 is the
 * only FE-observable "feature disabled" signal, since the personas surface is
 * NOT registered in the platform `is_feature_enabled` feature service the way
 * `findings` is.
 */
const isPersonasDisabledError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 503;

/**
 * Retry predicate for the capability probe, mirroring `useIsFeatureEnabledQuery`
 * so this hook doesn't silently broaden the app's retry behavior:
 *
 * - The 503 "feature disabled" signal is definitive — never retried.
 * - A terminal 4xx (404 unregistered / 403 not granted / etc.) can't change on
 *   retry, so it short-circuits instead of fanning out into `RETRY_LIMIT`
 *   identical requests + console errors. 429 (Too Many Requests) is the lone
 *   transient 4xx and keeps its retry budget.
 * - Test mode keeps the global QueryClient's no-retry behavior so a mocked
 *   5xx/network error doesn't start retrying and slow / flake CI.
 * - Everything else (other 5xx, network blips, 429) falls back to the same
 *   `RETRY_LIMIT` the global default would have applied.
 */
const shouldRetryPersonasConfig = (
  failureCount: number,
  error: unknown,
): boolean => {
  if (isPersonasDisabledError(error)) return false;

  const status = isAxiosError(error) ? error.response?.status : undefined;

  const isTerminalClientError =
    status !== undefined &&
    status >= 400 &&
    status < 500 &&
    status !== 429; /* 429 is transient — keep retrying */

  if (isTerminalClientError) return false;

  if (import.meta.env.MODE === 'test') return false;

  return failureCount < RETRY_LIMIT;
};

/**
 * Capability hook reporting whether the Developer Personas (Team Profiles)
 * feature is available for the current workspace. Used to gate the sidebar
 * entry, the `/team-profiles` routes, and the Research Preview reveal — one
 * signal so they can't drift.
 *
 * Two gates, AND-combined:
 *
 * 1. The per-workspace **Research Preview opt-in**
 *    (`research_preview.enable_developer_personas`, read from the engine
 *    config). This is the primary gate and defaults **off** — personas is a
 *    Research Preview feature that workspaces enable deliberately.
 * 2. The platform **master kill-switch** (the `/personas/config` 503 probe).
 *    Even with the toggle on, an explicit 503 (the `personas.enabled` master
 *    flag off platform-side) keeps the feature disabled, so a stale config
 *    value can't surface a service the platform has turned off.
 *
 * **Default off.** While either query is in flight the feature reports *not
 * enabled* (loading), so the default-off intent holds and the surfaces don't
 * flash on before the toggle resolves. The 503 probe stays fail-open on its
 * own axis (only an explicit 503 disables), but the AND with the toggle means
 * availability is ultimately driven by the opt-in.
 *
 * The probe targets `GET /personas/config` (a single workspace-config row
 * carrying the `enforce_personas_enabled` 503 gate) rather than the full
 * `/personas` catalog list, and the engine config is a shared cached query. The
 * probe is gated on the toggle being on, so workspaces that haven't opted in
 * (the default) fire no persona request at all from the broadly-mounted sidebar.
 */
export const usePersonasEnabled = (): {
  isPersonasEnabled: boolean;
  isPersonasLoading: boolean;
} => {
  const { data: configData, isLoading: isConfigLoading } = useConfigQuery();
  const isToggledOn = isDeveloperPersonasToggledOn(configData?.config);

  const {
    isLoading: isProbeLoading,
    isError: isProbeError,
    error: probeError,
  } = usePersonasConfigQuery({
    enabled: isToggledOn,
    retry: shouldRetryPersonasConfig,
  });

  const isMasterGateDisabled =
    isProbeError && isPersonasDisabledError(probeError);

  return {
    isPersonasEnabled: isToggledOn && !isMasterGateDisabled,
    isPersonasLoading: isConfigLoading || (isToggledOn && isProbeLoading),
  };
};
